import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Browser from '@wetransfer/concorde-browser';

import Analytics from '@module/analytics';
import Translations from '@module/i18n/dictionary';
import Actions from '@app/store/action';

import LanguagePickerItems, {
  propTypes as itemsPropTypes,
} from '@component/languagepicker/items';
import PopTip from '@component/info/poptip';
import Icon from '@component/icon';

import { OSLO_GRAY } from '@constant/colors';

export const LANGUAGES = Object.keys(Translations.locales).map((locale) => ({
  value: locale,
  label: Translations.locales[locale][0], // 0 = Native language, 1 = English
}));

export const LanguagePicker = ({
  languages,
  currentLanguage,
  changeLanguage,
}) => {
  const foundLanguage = languages.find(
    (language) => language.value === currentLanguage
  );

  if (!foundLanguage) {
    return null;
  }

  const handleLanguageChange = async (language) => {
    await changeLanguage(language);

    Analytics.trackSnowplowEvent({
      category: 'language',
      action: 'click_language_drop_down',
      label: language,
    });
  };

  const items = (
    <LanguagePickerItems
      languages={languages}
      currentLanguage={foundLanguage}
      changeLanguage={handleLanguageChange}
    />
  );

  return (
    <PopTip
      className="languagepicker"
      trigger="click"
      content={items}
      unique={true}
      anchor={Browser.isMobile ? 'top' : 'bottom'}
      offset={Browser.isMobile ? 15 : 10}
    >
      <span className="languagepicker__current">
        {foundLanguage.label}
        <Icon
          color={OSLO_GRAY}
          className="languagepicker__arrow"
          type="Arrow"
          orientation="bottom"
        />
      </span>
    </PopTip>
  );
};
LanguagePicker.propTypes = {
  languages: itemsPropTypes.languages,
  currentLanguage: PropTypes.string,
  changeLanguage: itemsPropTypes.changeLanguage,
};
LanguagePicker.defaultProps = {
  languages: LANGUAGES,
};

const mapStateToProps = ({ session: { language } }) => ({
  currentLanguage: language,
});

export const setDOMLanguage = (language) => {
  const docRoot = document.documentElement;
  if (docRoot && docRoot.hasAttribute('lang')) {
    docRoot.setAttribute('lang', language);
  }
};

const mapDispatchToProps = (dispatch) => ({
  changeLanguage: async (language) => {
    await dispatch(Actions.User.updateUserLanguage(language));

    // Update the session store and close poptip
    await dispatch(Actions.Session.setLanguage(language));

    // Update html[lang] if possible
    setDOMLanguage(language);
  },
});

const ConnectedLanguagePicker = connect(
  mapStateToProps,
  mapDispatchToProps
)(LanguagePicker);
ConnectedLanguagePicker.propTypes = {
  popupOptions: PropTypes.object,
};

export default ConnectedLanguagePicker;
